import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Card } from 'react-bootstrap';
import Layout from '../../components/ui/Layout';
import NoIndexHelmet from '../../components/seo/NoIndexHelmet';
import { useAuth } from '../../components/context/AuthContext';
import { fcmService } from '../../services/fcmService';
import { testSendNotification, checkFCMConfiguration } from '../../utils/testNotifications';
import { testCloudFunctionDirectly, testCloudFunctionAccess } from '../../utils/testCloudFunction';
import { testNotificationConfiguration } from '../../utils/testNotificationWorkaround';
import { testClientSideNotifications, requestNotificationPermission, testFCMTokenRegistration } from '../../utils/testClientSideNotifications';
import { testFunctionDirectAccess, testFirebaseFunctionsConfig } from '../../utils/testFunctionAccess';
import { testNotificationDirect, testFunctionEndpoint, testNotificationAlternative } from '../../utils/testNotificationDirect';
import { testEmployeeEndpoint, testEmployeeNotification } from '../../utils/testNotificationEmployee';
import { testNotificationBypass, testCompleteNotificationFlow } from '../../utils/testNotificationBypass';
import { createLogger } from '../../utils/logger';

const logger = createLogger('NotificationTest');

export default function NotificationTest() {
  const { currentUser } = useAuth();
  const [fcmToken, setFcmToken] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [testResult, setTestResult] = useState(null);
  const [configCheck, setConfigCheck] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cloudFunctionTest, setCloudFunctionTest] = useState(null);
  const [cloudFunctionLoading, setCloudFunctionLoading] = useState(false);
  const [workaroundTest, setWorkaroundTest] = useState(null);
  const [clientSideTest, setClientSideTest] = useState(null);
  const [clientSideLoading, setClientSideLoading] = useState(false);
  const [functionAccessTest, setFunctionAccessTest] = useState(null);
  const [functionAccessLoading, setFunctionAccessLoading] = useState(false);
  const [directTest, setDirectTest] = useState(null);
  const [directTestLoading, setDirectTestLoading] = useState(false);
  const [bypassTest, setBypassTest] = useState(null);
  const [bypassTestLoading, setBypassTestLoading] = useState(false);
  const [employeeTest, setEmployeeTest] = useState(null);
  const [employeeTestLoading, setEmployeeTestLoading] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);

  useEffect(() => {
    // Check notification permission
    setNotificationPermission(Notification.permission);
    
    // Check FCM configuration
    const config = checkFCMConfiguration();
    setConfigCheck(config);
    
    // Test Cloud Function access
    testCloudFunctionAccess()
      .then(result => {
        setCloudFunctionTest(result);
      })
      .catch(error => {
        setCloudFunctionTest({ success: false, error: error.message });
      });
    
    // Test notification configuration workaround
    const workaround = testNotificationConfiguration();
    setWorkaroundTest(workaround);
    
    // Test Firebase Functions configuration
    const functionConfig = testFirebaseFunctionsConfig();
    setFunctionAccessTest(functionConfig);
    
    // Try to get FCM token
    if (currentUser) {
      fcmService.requestPermissionAndGetToken()
        .then(token => {
          setFcmToken(token);
          logger.info('FCM Token obtained:', token);
        })
        .catch(error => {
          logger.error('Failed to get FCM token:', error);
        });
    }
  }, [currentUser]);

  const handleTestNotification = async () => {
    if (!currentUser) {
      setTestResult({ success: false, message: 'No user logged in' });
      return;
    }

    setLoading(true);
    try {
      const result = await testSendNotification(currentUser);
      setTestResult({ success: true, data: result });
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleTestCloudFunction = async () => {
    if (!currentUser) {
      setCloudFunctionTest({ success: false, message: 'No user logged in' });
      return;
    }

    setCloudFunctionLoading(true);
    try {
      const result = await testCloudFunctionDirectly(currentUser);
      setCloudFunctionTest({ success: true, data: result });
    } catch (error) {
      setCloudFunctionTest({ success: false, error: error.message });
    } finally {
      setCloudFunctionLoading(false);
    }
  };

  const handleClientSideTest = async () => {
    setClientSideLoading(true);
    try {
      const result = await testClientSideNotifications();
      setClientSideTest(result);
    } catch (error) {
      setClientSideTest({ success: false, error: error.message });
    } finally {
      setClientSideLoading(false);
    }
  };

  const handleRequestPermission = async () => {
    setClientSideLoading(true);
    try {
      const result = await requestNotificationPermission();
      setClientSideTest(result);
      setNotificationPermission(Notification.permission);
    } catch (error) {
      setClientSideTest({ success: false, error: error.message });
    } finally {
      setClientSideLoading(false);
    }
  };

  const handleFCMTokenTest = async () => {
    setClientSideLoading(true);
    try {
      const result = await testFCMTokenRegistration(fcmService);
      setClientSideTest(result);
    } catch (error) {
      setClientSideTest({ success: false, error: error.message });
    } finally {
      setClientSideLoading(false);
    }
  };

  const handleFunctionAccessTest = async () => {
    setFunctionAccessLoading(true);
    try {
      const result = await testFunctionDirectAccess();
      setFunctionAccessTest(result);
    } catch (error) {
      setFunctionAccessTest({ success: false, error: error.message });
    } finally {
      setFunctionAccessLoading(false);
    }
  };

  const handleDirectTest = async () => {
    setDirectTestLoading(true);
    try {
      const result = await testNotificationDirect(currentUser);
      setDirectTest(result);
    } catch (error) {
      setDirectTest({ success: false, error: error.message });
    } finally {
      setDirectTestLoading(false);
    }
  };

  const handleEndpointTest = async () => {
    setDirectTestLoading(true);
    try {
      const result = await testFunctionEndpoint();
      setDirectTest(result);
    } catch (error) {
      setDirectTest({ success: false, error: error.message });
    } finally {
      setDirectTestLoading(false);
    }
  };

  const handleAlternativeTest = async () => {
    setDirectTestLoading(true);
    try {
      const result = await testNotificationAlternative(currentUser);
      setDirectTest(result);
    } catch (error) {
      setDirectTest({ success: false, error: error.message });
    } finally {
      setDirectTestLoading(false);
    }
  };

  const handleBypassTest = async () => {
    setBypassTestLoading(true);
    try {
      const result = await testNotificationBypass(currentUser);
      setBypassTest(result);
    } catch (error) {
      setBypassTest({ success: false, error: error.message });
    } finally {
      setBypassTestLoading(false);
    }
  };

  const handleEmployeeEndpointTest = async () => {
    setEmployeeTestLoading(true);
    setEmployeeTest(null);
    try {
      const result = await testEmployeeEndpoint();
      setEmployeeTest(result);
    } catch (error) {
      setEmployeeTest({ success: false, error: error.message });
    } finally {
      setEmployeeTestLoading(false);
    }
  };

  const handleEmployeeNotificationTest = async () => {
    setEmployeeTestLoading(true);
    setEmployeeTest(null);
    try {
      const result = await testEmployeeNotification();
      setEmployeeTest(result);
    } catch (error) {
      setEmployeeTest({ success: false, error: error.message });
    } finally {
      setEmployeeTestLoading(false);
    }
  };

  const runComprehensiveDiagnostics = async () => {
    setDiagnosticsLoading(true);
    try {
      const diagnostics = {
        timestamp: new Date().toISOString(),
        browser: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          cookieEnabled: navigator.cookieEnabled,
          onLine: navigator.onLine
        },
        notifications: {
          permission: Notification.permission,
          supported: 'Notification' in window,
          maxActions: Notification.maxActions || 'unknown'
        },
        serviceWorker: {
          supported: 'serviceWorker' in navigator,
          registrations: []
        },
        fcm: {
          token: fcmToken,
          tokenLength: fcmToken?.length || 0,
          vapidKey: process.env.REACT_APP_FCM_VAPID_KEY ? 'Set' : 'Not set',
          vapidKeyLength: process.env.REACT_APP_FCM_VAPID_KEY?.length || 0
        },
        firebase: {
          config: {
            apiKey: process.env.REACT_APP_APIKEY ? 'Set' : 'Not set',
            authDomain: process.env.REACT_APP_AUTH_DOMAIN ? 'Set' : 'Not set',
            projectId: process.env.REACT_APP_PROJECT_ID ? 'Set' : 'Not set',
            storageBucket: process.env.REACT_APP_STORAGE_BUCKET ? 'Set' : 'Not set',
            messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID ? 'Set' : 'Not set',
            appId: process.env.REACT_APP_APP_ID ? 'Set' : 'Not set'
          }
        },
        user: {
          authenticated: !!currentUser,
          uid: currentUser?.uid || 'Not authenticated'
        }
      };

      // Check service worker registrations
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          diagnostics.serviceWorker.registrations = registrations.map(reg => ({
            scope: reg.scope,
            state: reg.active?.state || 'unknown',
            scriptURL: reg.active?.scriptURL || 'unknown'
          }));
        } catch (error) {
          diagnostics.serviceWorker.error = error.message;
        }
      }

      setDiagnostics(diagnostics);
    } catch (error) {
      setDiagnostics({ error: error.message });
    } finally {
      setDiagnosticsLoading(false);
    }
  };

  const handleCompleteFlowTest = async () => {
    setBypassTestLoading(true);
    try {
      const result = await testCompleteNotificationFlow(currentUser);
      setBypassTest(result);
    } catch (error) {
      setBypassTest({ success: false, error: error.message });
    } finally {
      setBypassTestLoading(false);
    }
  };

  return (
    <Layout>
      <NoIndexHelmet title="Notification Test | Pack My Cart" path="/notification-test" />
    <Container className="mt-4">
      <Row>
        <Col>
          <h2>Firebase Push Notification Test</h2>
          <p>This page helps test and debug the Firebase push notification system.</p>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={6}>
          <Card>
            <Card.Header>Notification Permission</Card.Header>
            <Card.Body>
              <p><strong>Status:</strong> {notificationPermission}</p>
              {notificationPermission === 'default' && (
                <Button onClick={handleRequestPermission} variant="primary">
                  Request Permission
                </Button>
              )}
              {notificationPermission === 'denied' && (
                <Alert variant="warning">
                  Notification permission is denied. Please enable it in your browser settings.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>FCM Token</Card.Header>
            <Card.Body>
              {fcmToken ? (
                <div>
                  <p><strong>Token:</strong> {fcmToken.substring(0, 50)}...</p>
                  <Alert variant="success">FCM token is registered</Alert>
                </div>
              ) : (
                <Alert variant="warning">No FCM token available</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col>
          <Card>
            <Card.Header>Configuration Check</Card.Header>
            <Card.Body>
              {configCheck && (
                <div>
                  <p><strong>Status:</strong> {configCheck.status}</p>
                  {configCheck.issues && configCheck.issues.length > 0 && (
                    <Alert variant="danger">
                      <strong>Critical Issues:</strong>
                      <ul>
                        {configCheck.issues.map((issue, index) => (
                          <li key={index}>{issue}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  {configCheck.warnings && configCheck.warnings.length > 0 && (
                    <Alert variant="info">
                      <strong>Warnings (non-critical):</strong>
                      <ul>
                        {configCheck.warnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  {configCheck.status === 'ok' && (
                    <Alert variant="success">Configuration looks good!</Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={6}>
          <Card>
            <Card.Header>Test Notification (via test utility)</Card.Header>
            <Card.Body>
              <Button 
                onClick={handleTestNotification} 
                disabled={loading || !currentUser}
                variant="primary"
              >
                {loading ? 'Sending...' : 'Send Test Notification'}
              </Button>
              
              {testResult && (
                <div className="mt-3">
                  {testResult.success ? (
                    <Alert variant="success">
                      <strong>Test successful!</strong>
                      <pre>{JSON.stringify(testResult.data, null, 2)}</pre>
                    </Alert>
                  ) : (
                    <Alert variant="danger">
                      <strong>Test failed:</strong> {testResult.message || testResult.error}
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>Test Cloud Function Directly</Card.Header>
            <Card.Body>
              <Button 
                onClick={handleTestCloudFunction} 
                disabled={cloudFunctionLoading || !currentUser}
                variant="secondary"
              >
                {cloudFunctionLoading ? 'Testing...' : 'Test Cloud Function'}
              </Button>
              
              {cloudFunctionTest && (
                <div className="mt-3">
                  {cloudFunctionTest.success ? (
                    <Alert variant="success">
                      <strong>Cloud Function test successful!</strong>
                      <pre>{JSON.stringify(cloudFunctionTest.data, null, 2)}</pre>
                    </Alert>
                  ) : (
                    <Alert variant="danger">
                      <strong>Cloud Function test failed:</strong> {cloudFunctionTest.message || cloudFunctionTest.error}
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={6}>
          <Card>
            <Card.Header>Client-Side Notification Test</Card.Header>
            <Card.Body>
              <div className="mb-3">
                <Button 
                  onClick={handleClientSideTest} 
                  disabled={clientSideLoading}
                  variant="primary"
                  className="me-2"
                >
                  {clientSideLoading ? 'Testing...' : 'Test Client-Side Notifications'}
                </Button>
                
                <Button 
                  onClick={handleRequestPermission} 
                  disabled={clientSideLoading}
                  variant="secondary"
                  className="me-2"
                >
                  {clientSideLoading ? 'Requesting...' : 'Request Permission'}
                </Button>
                
                <Button 
                  onClick={handleFCMTokenTest} 
                  disabled={clientSideLoading}
                  variant="info"
                >
                  {clientSideLoading ? 'Testing...' : 'Test FCM Token'}
                </Button>
              </div>
              
              {clientSideTest && (
                <div className="mt-3">
                  {clientSideTest.success ? (
                    <Alert variant="success">
                      <strong>Client-side test successful!</strong>
                      <p>{clientSideTest.message}</p>
                      {clientSideTest.token && <p><strong>Token:</strong> {clientSideTest.token}</p>}
                    </Alert>
                  ) : (
                    <Alert variant="danger">
                      <strong>Client-side test failed:</strong> {clientSideTest.message}
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Header>System Status (Workaround)</Card.Header>
            <Card.Body>
              {workaroundTest && (
                <div>
                  <Alert variant={workaroundTest.status === 'ready' ? 'success' : 'warning'}>
                    <strong>Status:</strong> {workaroundTest.message}
                  </Alert>
                  <div className="mt-3">
                    <h6>System Components:</h6>
                    <ul>
                      {Object.entries(workaroundTest.results).map(([key, value]) => (
                        <li key={key}><strong>{key}:</strong> {value}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={6}>
          <Card>
            <Card.Header>Function Access Diagnostics</Card.Header>
            <Card.Body>
              <div className="mb-3">
                <Button 
                  onClick={handleFunctionAccessTest} 
                  disabled={functionAccessLoading}
                  variant="warning"
                  className="me-2"
                >
                  {functionAccessLoading ? 'Testing...' : 'Test Direct Function Access'}
                </Button>
              </div>
              
              {functionAccessTest && (
                <div className="mt-3">
                  {functionAccessTest.success ? (
                    <Alert variant="success">
                      <strong>Function access test successful!</strong>
                      <pre>{JSON.stringify(functionAccessTest.data, null, 2)}</pre>
                    </Alert>
                  ) : (
                    <Alert variant="danger">
                      <strong>Function access test failed:</strong> {functionAccessTest.error || functionAccessTest.message}
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Header>CORS Bypass Tests</Card.Header>
            <Card.Body>
              <div className="mb-3">
                <Button 
                  onClick={handleDirectTest} 
                  disabled={directTestLoading || !currentUser}
                  variant="primary"
                  className="me-2 mb-2"
                >
                  {directTestLoading ? 'Testing...' : 'Direct HTTP Test'}
                </Button>
                
                <Button 
                  onClick={handleEndpointTest} 
                  disabled={directTestLoading}
                  variant="info"
                  className="me-2 mb-2"
                >
                  {directTestLoading ? 'Testing...' : 'Test Endpoint'}
                </Button>
                
                <Button 
                  onClick={handleAlternativeTest} 
                  disabled={directTestLoading || !currentUser}
                  variant="secondary"
                  className="mb-2"
                >
                  {directTestLoading ? 'Testing...' : 'Alternative Test'}
                </Button>
              </div>
              
              {directTest && (
                <div className="mt-3">
                  {directTest.success ? (
                    <Alert variant="success">
                      <strong>Direct test successful!</strong>
                      <pre>{JSON.stringify(directTest.data || directTest, null, 2)}</pre>
                    </Alert>
                  ) : (
                    <Alert variant="danger">
                      <strong>Direct test failed:</strong> {directTest.error || directTest.message}
                      {directTest.status && <p className="mb-0"><strong>Status:</strong> {directTest.status}</p>}
                      {directTest.corsEnabled !== undefined && (
                        <p className="mb-0"><strong>CORS Enabled:</strong> {directTest.corsEnabled ? 'Yes' : 'No'}</p>
                      )}
                      {directTest.data && (
                        <pre className="mt-2 mb-0 small">{JSON.stringify(directTest.data, null, 2)}</pre>
                      )}
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Header>Notification Bypass Tests</Card.Header>
            <Card.Body>
              <div className="mb-3">
                <Button 
                  onClick={handleBypassTest} 
                  disabled={bypassTestLoading || !currentUser}
                  variant="success"
                  className="me-2 mb-2"
                >
                  {bypassTestLoading ? 'Testing...' : 'Test Notification Bypass'}
                </Button>
                
                <Button 
                  onClick={handleCompleteFlowTest} 
                  disabled={bypassTestLoading || !currentUser}
                  variant="info"
                  className="mb-2"
                >
                  {bypassTestLoading ? 'Testing...' : 'Test Complete Flow'}
                </Button>
              </div>
              
              {bypassTest && (
                <div className="mt-3">
                  {bypassTest.success ? (
                    <Alert variant="success">
                      <strong>Bypass test successful!</strong>
                      <p>{bypassTest.message}</p>
                      {bypassTest.simulated && <p><em>This is a simulated notification for testing purposes.</em></p>}
                    </Alert>
                  ) : (
                    <Alert variant="danger">
                      <strong>Bypass test failed:</strong> {bypassTest.error || bypassTest.message}
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>Employee Notification Tests (notifyEmployeesNewOrderHttp)</Card.Header>
            <Card.Body>
              <p className="text-muted small mb-2">
                Tests the endpoint that notifies employees of new orders. Requires employees with role_name=Employee and FCM tokens.
              </p>
              <div className="mb-3">
                <Button
                  onClick={handleEmployeeEndpointTest}
                  disabled={employeeTestLoading}
                  variant="outline-info"
                  className="me-2 mb-2"
                >
                  {employeeTestLoading ? 'Testing...' : 'Test Endpoint (OPTIONS)'}
                </Button>
                <Button
                  onClick={handleEmployeeNotificationTest}
                  disabled={employeeTestLoading}
                  variant="warning"
                  className="mb-2"
                >
                  {employeeTestLoading ? 'Testing...' : 'Test Employee Notification'}
                </Button>
              </div>
              {employeeTest && (
                <div className="mt-3">
                  {employeeTest.success ? (
                    <Alert variant="success">
                      <strong>Employee test successful!</strong>
                      <pre className="mb-0 small">{JSON.stringify(employeeTest.data || employeeTest, null, 2)}</pre>
                    </Alert>
                  ) : (
                    <Alert variant="danger">
                      <strong>Employee test failed:</strong> {employeeTest.error || employeeTest.message}
                      {employeeTest.featureFlagDisabled && (
                        <p className="mb-0 mt-1"><em>Feature flag ENABLE_EMPLOYEE_NOTIFICATIONS may be disabled.</em></p>
                      )}
                      {employeeTest.noEmployees && (
                        <p className="mb-0 mt-1"><em>No employees with FCM tokens found. Employees must have role_name=Employee and an FCM token.</em></p>
                      )}
                      {employeeTest.data && (
                        <pre className="mt-2 mb-0 small">{JSON.stringify(employeeTest.data, null, 2)}</pre>
                      )}
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col>
          <Alert variant="info">
            <h5>How to Test:</h5>
            <ol>
              <li>Make sure you're logged in</li>
              <li>Grant notification permission if prompted</li>
              <li>Try the "Notification Bypass Tests" above to test client-side notifications</li>
              <li>Use "CORS Bypass Tests" to test the Cloud Function</li>
              <li>Check if you receive actual notifications</li>
            </ol>
            <p><strong>Note:</strong> The bypass tests work around Cloud Function issues and test the notification system directly.</p>
          </Alert>
        </Col>
      </Row>

      {/* Comprehensive Diagnostics */}
      <Row className="mt-4">
        <Card>
          <Card.Header>Comprehensive Diagnostics</Card.Header>
          <Card.Body>
            <div className="mb-3">
              <Button 
                onClick={runComprehensiveDiagnostics} 
                disabled={diagnosticsLoading}
                variant="info"
                className="me-2 mb-2"
              >
                {diagnosticsLoading ? 'Running Diagnostics...' : 'Run Comprehensive Diagnostics'}
              </Button>
            </div>
            
            {diagnostics && (
              <div>
                <h5>Diagnostic Results:</h5>
                <pre style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '15px', 
                  borderRadius: '5px',
                  fontSize: '12px',
                  maxHeight: '400px',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(diagnostics, null, 2)}
                </pre>
              </div>
            )}
          </Card.Body>
        </Card>
      </Row>
    </Container>
    </Layout>
  );
}
